/**
 * Shared formatting utilities for the application
 */

/**
 * Format a number as currency (ZAR)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  if (!value && value !== 0) return "R 0.00";

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) return "R 0.00";

  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/**
 * Format a date string to a readable format
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return "Not specified";

  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) return "Invalid date";

    return new Intl.DateTimeFormat("en-ZA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

/**
 * Format a date string to a short format (date only)
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string (date only)
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return "Not specified";

  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) return "Invalid date";

    return new Intl.DateTimeFormat("en-ZA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

/**
 * Format a number with thousand separators
 * @param {number|string} value - The value to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (value) => {
  if (!value && value !== 0) return "0";

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) return "0";

  return new Intl.NumberFormat("en-ZA").format(numValue);
};

/**
 * Format a percentage value
 * @param {number|string} value - The value to format (0-100)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (!value && value !== 0) return "0%";

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) return "0%";

  return `${numValue.toFixed(decimals)}%`;
};
